'use client';

import React, { createContext, useContext, PropsWithChildren } from 'react';
import { type FSLocale, translate, DEFAULT_LOCALE, localeLink } from '@fshr/shared';

type Translations = Record<string, string>;

type I18nContextProps = PropsWithChildren<{ translations: Translations; locale: FSLocale }>;

const I18nContext = createContext<{
	translations: Record<string, string>;
	locale: FSLocale;
}>({ translations: {}, locale: DEFAULT_LOCALE });

const I18nProvider = ({ translations, locale, children }: I18nContextProps) => {
	return (
		<I18nContext.Provider
			value={{
				translations,
				locale,
			}}
		>
			{children}
		</I18nContext.Provider>
	);
};

const useTranslations = () => {
	const context = useContext(I18nContext);

	return translate(context.translations);
};

const useLocale = () => {
	const context = useContext(I18nContext);
	return context.locale;
};

const useLocaleLink = () => {
	const context = useContext(I18nContext);
	return localeLink(context.locale);
};

const useI18n = () => {
	return {
		t: useTranslations(),
		locale: useLocale(),
		localeLink: useLocaleLink(),
	};
};

export { I18nProvider, useTranslations, useLocale, useI18n, useLocaleLink };
